<script setup>
import { scheduledReportsApi } from '@/api/ScheduledReports/ScheduledReportsApi.js'
import { onMounted, ref } from 'vue'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { useAuthStore } from '@/stores/authStore.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Util } from '@/models/utils/Util.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { Format } from '@/models/utils/FormatUtils.js'
import CopyTextModal from '@/components/Alerts/CopyTextModal.vue'

const toaster = new Toaster();
const authStore = useAuthStore()
const tenantSettings = ref(null)
const table = ref(null)
const isLoading = ref(false)

const showCopyModal = ref(false)
const dialogTitle = ref('')
const plainDialogMessage = ref('')
const formattedDialogMessage = ref('')

const columns = [
	{ field: "enabled", title: "Status", sort: true, width: '5%' },
	{ field: "report_type", title: "Type", sort: true, search: false },
	{ field: "name", title: "Name", sort: true, search: true },
	{ field: "description", title: "Description", sort: false, search: true },
	{ field: "recipients", title: "Recipients", sort: false, search: true, width: '5%', minWidth: '400px' },
	{ field: "run_at", title: "Next Run", sort: true, width: '5%' },
	{ field: "ran_at", title: "Last Run", sort: true, width: '5%' },
	{ field: "initiator_name", title: "Initiator", sort: true, width: '5%', search: true },
	{ field: "actions", title: "Actions", sort: false, search: false },
	{ field: "frequency", sort: true, search: true, hide: true },
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'scheduled-reports/admin/pending-reports',
		initialSortColumn: 'run_at',
		searchPlaceholder: 'Search',
		columns: columns,
	})
]

/**
 * Loads roles from the API.
 */
onMounted(() => {
	//isLoading.value = true
	//scheduledReportsApi.getScheduledReports(onGetScheduledReports)
	tenantSettings.value = authStore.getAuth.tenantSettings().scheduled_reports
})

/**
 * Gets the menu items for a scheduled report.
 * @returns {*[]}
 */
function getMenuItems()
{
	let items = [];
	Util.addActionMenuItem(items, 'delete','Delete')
	return items;
}

/**
 * Handles menu item selection.
 * @param p_menu_item
 * @param p_record
 */
function onMenuItemSelected(p_menu_item, p_record)
{
	switch (p_menu_item.name)
	{
		case "delete":
			deleteReport(p_record)
			break;
	}
}

/**
 * Deletes a scheduled report.
 * @param p_record
 */
function deleteReport(p_record)
{
	scheduledReportsApi.deleteReport(p_record, onStatusUpdated)
}

/**
 * Reloads the table to get the latest data.
 */
function reloadTable()
{
	//this will break of isLoading is true
	if (table.value)
	{
		table.value.loadData()
	}
	else
	{
		location.reload()
	}
}

/**
 * Handle response from a scheduled report status update.
 * @param p_response Response from server
 * @param p_type Type of update
 * @param p_error_eh Indicates an error occured while updating scheduled report status.
 */
function onStatusUpdated(p_response, p_type, p_error_eh)
{
	let success_message = "";
	let failed_message = "";
	try
	{
		switch (p_type)
		{
			case "edit":
				success_message = "Report status updated successfully.";
				failed_message = "Failed to update report status. Please try again.";
				break;
			case "delete":
				success_message = "Report deleted successfully.";
				failed_message = "Failed to delete report. Please try again.";
				break;
		}

		if (p_error_eh) //Uncaught error
		{
			p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			if (data && data.success)
			{
				toaster.success(success_message)
				reloadTable()
			}
			else if (data && !data.success) //Error
			{
				toaster.error(data.error_message)
			}
			else
			{
				toaster.error(failed_message)
			}
		}
	}
	catch (e)
	{
		toaster.error(failed_message)
		if (authStore.isDeveloper) console.error(e)
	}
}

/**
 * Shows the copy text dialog
 * @param title
 * @param message
 */
function showTextDialog(title, message) {
	dialogTitle.value = title
	plainDialogMessage.value = message
	formattedDialogMessage.value = message
	showCopyModal.value = true
}

/**
 *
 * @param p_recipients
 */
function showRecipients(p_recipients)
{
	let recipients = '';

	for (let i = 0; i < p_recipients.length; i++)
	{
		if (p_recipients[i].full_name)
		{
			recipients += p_recipients[i].full_name + ' (' +	p_recipients[i].email + ")\r\n";
		}
		else
		{
			recipients += p_recipients[i].email + "\r\n";
		}
	}

	showTextDialog('Recipients', recipients)
}
</script>

<template>
	<LoadingSpinner v-if="isLoading" />
	<div v-if="!isLoading">
		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
		>

			<template #enabled="data">
				<div v-if="data.value.error_message !== null">
					<div v-if="!authStore.isDeveloper" class="line-clamp-4 !text-error clickable-text overflow-ellipsis flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap bg-error-light text-error-on-color" @click="showTextDialog('Error', data.value.error_message)">
						Error
					</div>
					<div v-else class="flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap bg-error-light text-error-on-color">
						Error
					</div>
				</div>
				<div v-else>
					<div class="flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap bg-neutral-light text-label">
						Pending
					</div>
				</div>
			</template>

			<template #report_type="data">
				<div class="capitalize">
					{{ data.value.report_type }}
				</div>
			</template>

			<template #name="data">
				{{ data.value.name}}
			</template>

			<template #description="data">
				<div class="whitespace-pre-wrap">
					{{ data.value.description}}
				</div>
			</template>

			<template #recipients="data">
				<div v-if="data.value.recipients && data.value.recipients.length > 0" class="clickable-text overflow-ellipsis whitespace-nowrap" @click="showRecipients(data.value.recipients)">
					<div v-for="(recipient, index) in data.value.recipients" :key="index">
						<span :class="[{'line-through': !recipient.is_active}]"><b>{{ recipient.full_name }}</b> ({{ recipient.email }})</span><span v-if="!recipient.is_active"> (Inactive)</span>
					</div>
				</div>
				<div v-else>
					There are no active recipients for this report.
				</div>
			</template>

			<template #run_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<div v-if="data.value.run_at">
						{{ Format.date(data.value.run_at) }}
					</div>
					<div v-else>
						-
					</div>
				</div>
			</template>

			<template #ran_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<div v-if="data.value.ran_at">
						{{ Format.date(data.value.ran_at) }}
					</div>
					<div v-else>
						-
					</div>
				</div>
			</template>

			<template #initiator_name="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					{{ data.value.initiator?.full_name ?? 'Portal' }}
				</div>
			</template>

			<template #actions="data">
				<div v-if="data.value.frequency === null" class="flex w-full justify-center">
					<EndpointTableCellMenu
						:items="getMenuItems()"
						:record="data.value"
						@onMenuItemSelected="onMenuItemSelected"
						:disabled="data.value.error_message !== null"
					/>
				</div>
			</template>
		</PortalDataTable>
	</div>

	<CopyTextModal
		:show="showCopyModal"
		:title="dialogTitle"
		:message="plainDialogMessage"
		:formattedMessage="formattedDialogMessage"
		@close="showCopyModal=false"
	/>
</template>

<style scoped>

</style>